/* Peak Moto collection filters.
 *
 * Loads every product in the collection from the pmo-data view, then filters,
 * sorts and draws the grid in the browser so counts update as you click and
 * no option can lead to an empty page. The rider's measurements, gender and
 * riding temperatures live in PmoFit's profile and carry across collections.
 * The server-rendered grid stays in place until the data arrives, and is
 * what shows without JavaScript.
 */
(function () {
  "use strict";

  var rootEl = document.querySelector("[data-pmo-col]");
  if (!rootEl || !window.PmoFit) return;
  var F = window.PmoFit;
  var CFG = {};
  try { CFG = JSON.parse(rootEl.querySelector("[data-pmo-cfg]").textContent); } catch (e) {}
  var PER_PAGE = CFG.perPage || 24;
  var CONSULT_URL = "https://peakmotogear.com.au/pages/fit-consultation";
  // Sizes only show once the shopper has ticked that they're a guide. Kept
  // in this browser so it's asked once.
  var ACK_KEY = "pmo.fitAck.v1";
  var acked = false;
  try { acked = localStorage.getItem(ACK_KEY) === "1"; } catch (e) {}
  // The size-guide note under the fit bar can be dismissed for good.
  var NOTE_KEY = "pmo.fitNoteOff.v1", noteOff = false;
  try { noteOff = localStorage.getItem(NOTE_KEY) === "1"; } catch (e) {}
  // Shoppers who don't want to use the fit tools can fold the bar away; while
  // it's folded, their measurements aren't applied.
  var BAR_KEY = "pmo.fitBarOff.v1", barOff = false;
  try { barOff = localStorage.getItem(BAR_KEY) === "1"; } catch (e) {}
  function setBarOff(on) {
    barOff = on;
    try { if (on) localStorage.setItem(BAR_KEY, "1"); else localStorage.removeItem(BAR_KEY); } catch (e) {}
  }
  function setAck(on) {
    acked = on;
    try { if (on) localStorage.setItem(ACK_KEY, "1"); else localStorage.removeItem(ACK_KEY); } catch (e) {}
  }
  // Info box used for the size-suggestion disclaimer.
  function infoBox(inner, cls) {
    return '<div class="pmo-info' + (cls ? " " + cls : "") + '" role="note" style="display:flex;align-items:flex-start;gap:10px;background:#eef1f5;border-left:3px solid #3a506b;padding:12px 14px;margin:' + (cls ? "0 0 18px" : "14px 0") + ';font-size:14px;line-height:1.5;color:#2c3440">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3a506b" stroke-width="2" stroke-linecap="round" aria-hidden="true" style="flex:none;margin-top:2px"><circle cx="12" cy="12" r="9.5"/><path d="M12 11v6"/><circle cx="12" cy="7.5" r=".6" fill="#3a506b"/></svg>' +
      '<p style="margin:0">' + inner.replace(/<a /g, '<a style="color:#3a506b;font-weight:600;text-decoration:underline;text-underline-offset:3px" ') + "</p></div>";
  }

  // ---------- small helpers ----------

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function uniq(a) {
    var seen = {};
    return a.filter(function (x) { var k = typeof x + ":" + x; if (seen[k]) return false; seen[k] = 1; return true; });
  }
  function money(cents) {
    var d = cents / 100;
    return "$" + d.toLocaleString("en-AU", { minimumFractionDigits: d % 1 ? 2 : 0, maximumFractionDigits: 2 });
  }
  function listText(a) {
    return a.length > 1 ? a.slice(0, -1).join(", ") + " and " + a[a.length - 1] : a[0] || "";
  }
  function pluralNoun(n) {
    return n === 1 ? CFG.nounOne || "product" : CFG.noun || "products";
  }

  // ---------- categories and measurement fields ----------

  function catOf(type, title) {
    var t = (type || "") + " " + (title || "");
    if (/visor|cleaning|care\b/i.test(type)) return "other";
    if (/helmet/i.test(t)) return "helmets";
    if (/sock/i.test(type)) return "other";
    if (/boot|shoe/i.test(t)) return "boots";
    if (/glove/i.test(t)) return "gloves";
    if (/suit/i.test(type)) return "suits";
    if (/pants|jean|trouser|legging|chaps/i.test(t)) return "pants";
    if (/thermal|base ?layer/i.test(t)) return "base";
    if (/jacket|shirt|hoodie|windbreaker|vest|rain|jersey|heated/i.test(t)) return "jackets";
    if (/armour|protector|slider/i.test(type)) return "protection";
    return "other";
  }
  var CAT_LABEL = { jackets: "Jackets & tops", pants: "Pants & jeans", suits: "Suits", base: "Base layers", gloves: "Gloves", boots: "Boots & shoes", helmets: "Helmets", protection: "Protection", other: "Accessories & more" };
  var CAT_NOUN = { jackets: "jacket", pants: "pant", suits: "suit", base: "base layer", gloves: "glove", boots: "boot", helmets: "helmet" };
  var CAT_ORDER = ["jackets", "pants", "suits", "base", "gloves", "boots", "helmets", "protection", "other"];
  var CAT_FIELDS = { jackets: ["chest", "waist"], base: ["chest", "waist", "hips"], suits: ["chest", "waist", "hips", "inseam"], pants: ["waist", "hips", "inseam"], gloves: ["palm", "hand length"], boots: ["foot"], helmets: ["head"] };
  var FIELD_ORDER = ["chest", "waist", "hips", "inseam", "palm", "hand length", "foot", "head"];
  var FIELD = {
    chest: ["Chest", "e.g. 102", "Around the fullest part, tape level under your arms."],
    waist: ["Waist", "e.g. 86", "Around your natural waist, above the belly button."],
    hips: ["Hips", "e.g. 100", "Around the widest part of your hips and seat."],
    inseam: ["Inseam", "e.g. 79", "From the top of your inside leg to the floor, standing in socks. Optional: it picks your length."],
    palm: ["Palm", "e.g. 21", "Around your palm just below the knuckles, thumb out of the way."],
    "hand length": ["Hand length", "e.g. 19", "From the wrist crease to the tip of your middle finger. Glove charts don't account for finger length."],
    foot: ["Foot length", "e.g. 26.5", "Heel to longest toe, standing on paper against a wall. Boot charts only go by length, so wide feet or a high instep may need a size up."],
    head: ["Head", "e.g. 58", "Around your head, about 2cm above your eyebrows."]
  };
  // What the size guide's measuring tip doesn't say, added after it.
  var TIP_EXTRA = { inseam: "Optional: it picks your length.", "hand length": "Glove charts don't account for finger length.", foot: "Boot charts only go by length, so wide feet or a high instep may need a size up." };
  var FIG_GROUPS = [["chest", "waist", "hips", "inseam"], ["palm", "hand length"], ["foot"], ["head"]];
  function figGroup(k) {
    for (var i = 0; i < FIG_GROUPS.length; i++) if (FIG_GROUPS[i].indexOf(k) > -1) return i;
    return -1;
  }
  // The figure and tips come from the product page size guide script, which
  // the section loads before this file.
  function tipText(k) {
    var UI = window.PmoMeasureUI;
    if (!UI) return esc(FIELD[k][2]);
    return UI.tipHtml(FIELD[k][0]) + (TIP_EXTRA[k] ? " " + esc(TIP_EXTRA[k]) : "");
  }
  // The figure for the box in use (or the first box), and its measurements.
  function figAreas(fields, k) {
    var g = figGroup(k || fields[0]);
    return fields.filter(function (f) { return figGroup(f) === g; });
  }

  // ---------- colours ----------

  var FAMILY = [
    ["Pattern", /camo|pattern|print|leopard|tiger|check|plaid|holo|multi/i, "repeating-linear-gradient(45deg,#6b6a52 0 3px,#b9b48e 3px 6px)"],
    ["Black", /black|stealth|jet|onyx|ebony|anthracite black/i, "#1d1f22"],
    ["White", /white|ivory|cream|off-white|pearl/i, "#f3f3f1"],
    ["Grey", /gr[ae]y|silver|charcoal|anthracite|graphite|slate|ash|titanium|gunmetal|smoke/i, "#8e959d"],
    ["Sand", /sand|beige|khaki|stone|camel|tan\b|taupe|desert|bone/i, "#c9b38b"],
    ["Brown", /brown|cognac|chocolate|coffee|mocha|tobacco|whisky|rust|chestnut|bronze/i, "#7a4b2b"],
    ["Blue", /blue|navy|denim|indigo|teal|petrol|cobalt|sky|aqua|cyan/i, "#2f5180"],
    ["Green", /green|olive|army|forest|lime|mint|sage/i, "#536b3c"],
    ["Red", /red|burgundy|wine|maroon|oxblood|cherry|bordeaux/i, "#a3302f"],
    ["Orange", /orange|amber/i, "#dd7a2c"],
    ["Yellow", /yellow|neon|fluo|hi[- ]?vi|gold|mustard/i, "#e0b42a"],
    ["Pink", /pink|rose|blush|magenta|fuchsia/i, "#e08fb2"],
    ["Purple", /purple|violet|lilac|lavender|plum/i, "#5d3c7d"]
  ];
  var FAMILY_HEX = {};
  FAMILY.forEach(function (f) { FAMILY_HEX[f[0]] = f[2]; });
  var SWATCH = {};
  function colourParts(name) {
    return String(name || "").split(/\s*(?:\/|\+|&|,|\band\b| - )\s*/i).filter(Boolean);
  }
  function familyOf(part) {
    for (var i = 0; i < FAMILY.length; i++) if (FAMILY[i][1].test(part)) return FAMILY[i][0];
    return null;
  }
  function hexOf(part) {
    var k = norm(part);
    if (SWATCH[k]) return SWATCH[k];
    var f = familyOf(part);
    return f ? FAMILY_HEX[f] : "#d9dee5";
  }
  function norm(s) { return String(s == null ? "" : s).trim().toLowerCase(); }
  // A swatch value is a colour (#hex) or a swatch image in the theme's assets.
  function swatchCss(v) {
    if (/\.(png|jpe?g|gif|webp|svg)$/i.test(v)) return "url('" + (CFG.assetBase || "") + encodeURIComponent(v) + "') center/cover";
    return v;
  }
  function swatchBg(name, parts) {
    var whole = SWATCH[norm(name)];
    if (whole) return swatchCss(whole);
    var hexes = parts.map(hexOf);
    if (hexes.length > 1 && hexes[0].charAt(0) === "#" && hexes[1].charAt(0) === "#") return "linear-gradient(135deg," + hexes[0] + " 50%," + hexes[1] + " 50%)";
    return swatchCss(hexes[0] || "#d9dee5");
  }

  // ---------- protection ----------

  function protectionOf(list) {
    var out = null;
    (list || []).forEach(function (s) {
      var m;
      if (out) return;
      if ((m = String(s).match(/CE (AAA|AA|A) Rated/i))) out = "CE " + m[1].toUpperCase();
      else if (/\bB Rated/i.test(s)) out = "CE B";
      else if (/\bC Rated/i.test(s)) out = "CE C";
      else if ((m = String(s).match(/Gloves CE Certified.*Level (\d)( KP)?/i))) out = "CE Level " + m[1] + (m[2] ? " KP" : "");
      else if (/Footwear CE Certified/i.test(s)) out = "CE certified";
      else if (/ECE R?\s?22\.06/i.test(s)) out = "ECE 22.06";
    });
    // Armour (back protectors, knee/elbow inserts...) shows its highest level.
    if (!out) (list || []).forEach(function (s) {
      var m = String(s).match(/CE Level (\d) (Back|Shoulder|Elbow|Chest|Hip|Knee)/i);
      if (m && (!out || m[1] > out.slice(-1))) out = "CE Level " + m[1];
    });
    return out;
  }
  // A garment, glove or boot rating of its own, as opposed to armour inside.
  function hasOwnRating(list) {
    return (list || []).some(function (s) { return /CE (AAA|AA|A|B|C) Rated|\b[BC] Rated|Gloves CE Certified|Footwear CE Certified/i.test(String(s)); });
  }
  // Australian brands don't need CE ratings: their protective gear without a
  // rating of its own says so instead of showing the armour's level.
  var NOCE_CATS = { jackets: 1, pants: 1, suits: 1, base: 1, gloves: 1 };
  var PROT_ORDER = ["CE AAA", "CE AA", "CE A", "CE B", "CE C", "CE Level 2 KP", "CE Level 2", "CE Level 1 KP", "CE Level 1", "CE certified", "ECE 22.06"];

  // ---------- state ----------

  var profile = F.loadProfile();
  var S = {
    items: null,
    fit: null, fitLoading: false,
    sel: { cat: [], gen: [], size: [], len: [], prot: [], wp: [], rs: [], col: [], brand: [] },
    pLo: null, pHi: null, inStock: false, sort: "auto", fitsOnly: true,
    tempOn: !!(profile.temp && profile.temp.on), tLo: profile.temp ? profile.temp.lo : 8, tHi: profile.temp ? profile.temp.hi : 22,
    shown: PER_PAGE, open: {}, fitOpen: false, sheet: null, tipField: null
  };
  // The saved gender only applies where a collection has both women's and
  // men's gear (checked once the products load), never from a link's filters.
  var genFromProfile = false;
  if (profile.gen && profile.gen.length) { S.sel.gen = profile.gen.slice(); genFromProfile = true; }
  readUrl();

  function readUrl() {
    var q = new URLSearchParams(location.search);
    Object.keys(S.sel).forEach(function (k) {
      var v = q.get("f_" + k);
      if (v) { S.sel[k] = v.split("~").filter(Boolean); if (k === "gen") genFromProfile = false; }
    });
    var pr = q.get("f_price");
    if (pr) { var a = pr.split("-"); S.pLo = +a[0] || null; S.pHi = +a[1] || null; }
    if (q.get("f_stock") === "1") S.inStock = true;
    if (q.get("sort_by_fit")) S.sort = q.get("sort_by_fit");
  }
  function writeUrl() {
    var q = new URLSearchParams(location.search);
    Array.from(q.keys()).forEach(function (k) { if (/^f_|^sort_by_fit$/.test(k)) q.delete(k); });
    Object.keys(S.sel).forEach(function (k) { if (S.sel[k].length) q.set("f_" + k, S.sel[k].join("~")); });
    if (S.pLo != null || S.pHi != null) q.set("f_price", (S.pLo || "") + "-" + (S.pHi || ""));
    if (S.inStock) q.set("f_stock", "1");
    if (S.sort !== "auto") q.set("sort_by_fit", S.sort);
    q.delete("page");
    var s = q.toString();
    try { history.replaceState(null, "", location.pathname + (s ? "?" + s : "")); } catch (e) {}
  }
  function saveRider() {
    profile.temp = { on: S.tempOn, lo: S.tLo, hi: S.tHi };
    profile.gen = S.sel.gen.slice();
    F.saveProfile(profile);
  }

  // ---------- data ----------

  function base() {
    return (CFG.url || location.pathname).replace(/\?.*$/, "");
  }
  function getPage(view, n) {
    return fetch(base() + "?view=" + view + "&page=" + n, { credentials: "same-origin" }).then(function (r) { return r.json(); });
  }
  function getPages(view) {
    var known = Math.min(Math.ceil((CFG.count || 0) / 250), 20);
    if (known > 1) {
      var all = [];
      for (var p = 1; p <= known; p++) all.push(getPage(view, p));
      return Promise.all(all);
    }
    return getPage(view, 1).then(function (first) {
      var pages = Math.min(first.pages || 1, 20);
      var rest = [];
      for (var i = 2; i <= pages; i++) rest.push(getPage(view, i));
      return Promise.all(rest).then(function (more) { return [first].concat(more); });
    });
  }

  function prep(raw, idx) {
    var o = (raw.o || []).map(function (n) { return String(n); });
    var cat = catOf(raw.ty, raw.t);
    var sizeIdx = -1, lenIdx = -1, colIdx = -1;
    o.forEach(function (n, i) {
      if (/colou?r/i.test(n)) colIdx = i;
      else if (/length|leg|inseam/i.test(n)) lenIdx = i;
      else if (/size/i.test(n) && sizeIdx < 0) sizeIdx = i;
    });
    if (sizeIdx < 0 && o.length === 1 && colIdx < 0 && lenIdx < 0 && !/^title$/i.test(o[0])) sizeIdx = 0;
    var ctx = cat === "suits" ? "jackets" : cat;
    if (sizeIdx > -1 && /shoe|boot/i.test(o[sizeIdx])) ctx = "boots";
    if (sizeIdx > -1 && /pant|waist/i.test(o[sizeIdx])) ctx = "pants";
    var vars = (raw.vs || []).map(function (v) { return { o: [v[0], v[1], v[2]], a: !!v[3], id: v[4] }; });
    var chips = {};
    var lens = {};
    var cols = [];
    vars.forEach(function (v) {
      if (sizeIdx > -1 && cat !== "other" && cat !== "protection") F.chipsOf(v.o[sizeIdx], ctx).forEach(function (c) {
        var k = c.sys + ":" + c.v;
        if (!chips[k]) chips[k] = { sys: c.sys, v: c.v, a: false };
        if (v.a) chips[k].a = true;
        v.chips = (v.chips || []).concat([k]);
      });
      var lb = lenIdx > -1 ? F.lengthBucket(v.o[lenIdx]) : sizeIdx > -1 ? F.lengthBucket(String(v.o[sizeIdx] || "").replace(/^[^\/]*/, "")) : null;
      if (lb) { v.len = lb; lens[lb] = lens[lb] || v.a; }
      if (colIdx > -1 && v.o[colIdx] && cols.indexOf(v.o[colIdx]) < 0) cols.push(v.o[colIdx]);
    });
    var colours = cols.map(function (n) {
      var parts = colourParts(n);
      var fams = uniq(parts.map(familyOf).filter(Boolean));
      return { n: n, fams: fams.length ? fams : [], bg: swatchBg(n, parts) };
    });
    var gen = (raw.g || []).filter(function (g) { return g === "Women" || g === "Men" || g === "Unisex"; });
    return {
      i: idx, id: raw.id, h: raw.h, t: raw.t, u: raw.u, v: raw.v || "", img: raw.img, p: raw.p, pm: raw.pm, cp: raw.cp,
      av: raw.av, pub: raw.pub || 0, cat: cat, ctx: ctx, gen: gen,
      tp: raw.tp && raw.tp.length ? raw.tp : null,
      rs: raw.rs && raw.rs.length ? raw.rs : null,
      wp: raw.wp === true ? true : raw.wp === false ? false : null,
      prot: protectionOf(raw.ip), noCE: !!raw.au && !!NOCE_CATS[cat] && !hasOwnRating(raw.ip) && (raw.ip || []).some(function (s) { return /CE Level|Australian brand|Armou?r/i.test(String(s)); }), rv: +raw.rv || 0, rc: +raw.rc || 0, sc: !!raw.sc, est: !!raw.est,
      sizeIdx: sizeIdx, lenIdx: lenIdx, colIdx: colIdx, vars: vars,
      chips: chips, lens: lens, colours: colours, fit: null
    };
  }

  function loadData() {
    var sw = CFG.swatches ? fetch(CFG.swatches).then(function (r) { return r.json(); }).then(function (d) {
      (d.colors || []).forEach(function (o) { Object.keys(o).forEach(function (k) { SWATCH[norm(k)] = o[k]; }); });
    }).catch(function () {}) : Promise.resolve();
    Promise.all([getPages("pmo-data"), sw]).then(function (res) {
      var items = [];
      res[0].forEach(function (pg) { (pg.items || []).forEach(function (raw) { items.push(prep(raw, items.length)); }); });
      S.items = items;
      // A men's or women's collection with the odd mis-tagged product still
      // counts as one gender: each side needs at least 15% of the products.
      var onlyW = items.filter(function (it) { return it.gen.indexOf("Women") > -1 && it.gen.indexOf("Men") < 0; }).length;
      var onlyM = items.filter(function (it) { return it.gen.indexOf("Men") > -1 && it.gen.indexOf("Women") < 0; }).length;
      var both = onlyW + onlyM;
      var mixed = both && Math.min(onlyW, onlyM) / both >= 0.15;
      if (genFromProfile && !mixed) S.sel.gen = [];
      if (fieldsActive().length) loadFit(); else render();
    }).catch(function () {});
  }

  function loadFit() {
    if (S.fit || S.fitLoading) { computeFit(); render(); return; }
    S.fitLoading = true;
    render();
    getPages("pmo-fit").then(function (pages) {
      var fit = {};
      pages.forEach(function (pg) { Object.keys(pg.items || {}).forEach(function (h) { fit[h] = pg.items[h]; }); });
      S.fit = fit;
      S.fitLoading = false;
      computeFit();
      render();
    }).catch(function () { S.fitLoading = false; S.fit = {}; render(); });
  }

  // ---------- fit ----------

  function collectionFields() {
    if (!S.items) return [];
    var cats = uniq(S.items.filter(function (it) { return it.sc; }).map(function (it) { return it.cat; }));
    var want = {};
    cats.forEach(function (c) { (CAT_FIELDS[c] || []).forEach(function (k) { want[k] = 1; }); });
    return FIELD_ORDER.filter(function (k) { return want[k]; });
  }
  function fieldsActive() {
    return collectionFields().filter(function (k) { return F.measValue(profile.meas[k]) != null; });
  }
  function fitActive() {
    return !barOff && acked && !!S.fit && fieldsActive().length > 0;
  }
  function computeFit() {
    if (!S.items) return;
    S.items.forEach(function (it) {
      it.fit = null;
      if (!S.fit || !S.fit[it.h]) return;
      var f = F.fitProduct(S.fit[it.h], profile.meas);
      if (!f) return;
      var vars = F.variantsFor(it, f.label);
      // The chart's size isn't one this product comes in: it doesn't fit.
      if (it.sizeIdx > -1 && !vars.length) {
        var c = F.chipsOf((F.splitLength(f.label) || { size: f.label }).size, it.ctx)[0];
        f.missing = c ? c.v : f.label;
        f.level = "out";
        f.span = null;
      }
      f.vars = vars;
      f.a = vars.some(function (v) { return v.a; });
      var v0 = vars.filter(function (v) { return v.a; })[0] || vars[0];
      f.sizeText = v0 ? sizeTextOf(it, v0) : f.label;
      it.fit = f;
    });
  }
  function sizeTextOf(it, v) {
    var s = String(v.o[it.sizeIdx] || "");
    // The product's own length name ("Tall 33\"" shows as Tall), not the
    // filter group it falls under.
    if (it.lenIdx > -1 && v.o[it.lenIdx]) s += " · " + (String(v.o[it.lenIdx]).replace(/\s*\(?\s*\d+(?:\.\d+)?\s*(?:"|”|''|in\b|inch(?:es)?)\s*\)?\s*$/i, "").trim() || v.len || v.o[it.lenIdx]);
    return s;
  }
  function fits(it) {
    return it.fit && (it.fit.level === "great" || it.fit.level === "good");
  }
  // "Fits me" only hides what a size chart says won't fit. Products with no
  // chart to match stay, after the ones we could match.
  function hiddenByFit(it) {
    return !!it.fit && !fits(it);
  }

  // ---------- temperature ----------

  var EDGE = [8, 15, 22, 30];
  var BANDS = ["Cold", "Cool", "Mild", "Warm", "Hot"];
  function bandOf(d) { for (var i = 0; i < EDGE.length; i++) if (d < EDGE[i]) return i; return 4; }
  function covers(it) {
    if (!it.tp) return true;
    // Any overlap counts: the card shows the garment's own range.
    for (var b = bandOf(S.tLo); b <= bandOf(S.tHi); b++) if (it.tp.indexOf(BANDS[b]) > -1) return true;
    return false;
  }
  function rangeText(tp) {
    var idx = tp.map(function (n) { return BANDS.indexOf(n); }).filter(function (i) { return i > -1; });
    if (!idx.length) return "";
    var lo = Math.min.apply(null, idx), hi = Math.max.apply(null, idx);
    if (lo === 0 && hi === 4) return "All temperatures";
    if (lo === 0) return "Under " + EDGE[hi] + "°C";
    if (hi === 4) return EDGE[lo - 1] + "°C and up";
    return EDGE[lo - 1] + "–" + EDGE[hi] + "°C";
  }
  var THERMO = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 14.76V4a2 2 0 0 0-4 0v10.76a4 4 0 1 0 4 0z"/></svg>';
  function tempLive() { return S.tempOn; }
  function tempText() { return S.tempOn ? S.tLo + "–" + S.tHi + "°C" : "Any"; }

  // ---------- filtering ----------

  var GEN_LABEL = { Women: "Women's", Men: "Men's", Unisex: "Unisex" };
  var MATCH = {
    cat: function (it, v) { return it.cat === v; },
    gen: function (it, v) { return it.gen.indexOf(v) > -1 || (v !== "Unisex" && it.gen.indexOf("Unisex") > -1); },
    size: function (it, v) { var c = it.chips[v]; return !!c && (!S.inStock || c.a); },
    len: function (it, v) { return it.lens[v] != null && (!S.inStock || it.lens[v]); },
    prot: function (it, v) { return (it.prot || "None") === v; },
    wp: function (it, v) { return v === "Waterproof" ? it.wp === true : it.wp === false; },
    rs: function (it, v) { return !it.rs || it.rs.indexOf(v) > -1; },
    col: function (it, v) { return it.colours.some(function (c) { return c.fams.indexOf(v) > -1; }); },
    brand: function (it, v) { return it.v === v; }
  };
  function priceBounds() {
    var ps = S.items.map(function (it) { return it.p / 100; });
    return { min: Math.floor(Math.min.apply(null, ps) / 10) * 10, max: Math.ceil(Math.max.apply(null, ps) / 10) * 10 };
  }
  function mkPass(st) {
    var fa = fitActive();
    var pb = priceBounds();
    var lo = st.pLo == null ? pb.min : st.pLo, hi = st.pHi == null ? pb.max : st.pHi;
    return function (it, skip) {
      for (var k in st.sel) {
        if (k === skip || !st.sel[k].length) continue;
        var any = false;
        for (var j = 0; j < st.sel[k].length; j++) if (MATCH[k](it, st.sel[k][j])) { any = true; break; }
        if (!any) return false;
      }
      if (skip !== "temp" && st.tempOn && !covers(it)) return false;
      if (skip !== "fit" && fa && st.fitsOnly && hiddenByFit(it)) return false;
      if (skip !== "price" && (it.p / 100 < lo || it.p / 100 > hi)) return false;
      if (skip !== "stock" && st.inStock) {
        if (fa && it.fit && it.fit.vars.length) { if (!it.fit.a) return false; }
        else if (!it.av) return false;
      }
      return true;
    };
  }

  // ---------- render ----------

  var $ = function (sel) { return rootEl.querySelector(sel); };
  var regions = {
    fitbar: $("[data-pmo-fitbar]"),
    rail: $("[data-pmo-rail]"),
    results: $("[data-pmo-results]")
  };

  function render() {
    if (!S.items) return;
    rootEl.classList.add("pmo-col--live");
    var view = compute();
    // While a measurement is being typed, its box is left alone: redrawing it
    // would move the cursor and rewrite what's half typed. It catches up when
    // the box closes.
    if (!typingMeas()) renderFitbar(view);
    renderRail(view);
    renderResults(view);
    writeUrl();
  }

  function typingMeas() {
    var a = document.activeElement;
    return S.fitOpen && !!a && a.hasAttribute && a.hasAttribute("data-meas") && rootEl.contains(a);
  }

  function compute() {
    var D = S.items;
    var pass = mkPass(S);
    var fa = fitActive();
    function opts(k, values, label) {
      return values.map(function (v) {
        var n = 0;
        for (var i = 0; i < D.length; i++) if (pass(D[i], k) && MATCH[k](D[i], v)) n++;
        var on = S.sel[k].indexOf(v) > -1;
        return { k: k, v: v, label: label ? label(v) : v, n: n, on: on, dis: !on && n === 0 };
      });
    }
    var cats = CAT_ORDER.filter(function (c) { return D.some(function (it) { return it.cat === c; }); });
    var gens = ["Women", "Men", "Unisex"].filter(function (g) { return D.some(function (it) { return it.gen.indexOf(g) > -1; }); });
    if (gens.indexOf("Unisex") > -1 && gens.length === 1) gens = ["Women", "Men", "Unisex"];
    var chipKeys = {};
    D.forEach(function (it) { Object.keys(it.chips).forEach(function (k) { chipKeys[k] = it.chips[k]; }); });
    var sizeVals = Object.keys(chipKeys).sort(function (a, b) { return F.chipRank(chipKeys[a]) - F.chipRank(chipKeys[b]); });
    var lens = ["Short", "Regular", "Long"].filter(function (l) { return D.some(function (it) { return it.lens[l] != null; }); });
    var prots = PROT_ORDER.filter(function (p) { return D.some(function (it) { return it.prot === p; }); });
    if (prots.length && D.some(function (it) { return !it.prot && it.cat !== "other"; })) prots.push("None");
    var rsVals = ["Sport & Track", "Touring", "Adventure & Off-Road", "Urban & Commuting", "Cruiser", "Heritage"].filter(function (r) { return D.some(function (it) { return it.rs && it.rs.indexOf(r) > -1; }); });
    var fams = FAMILY.map(function (f) { return f[0]; }).filter(function (f) { return D.some(function (it) { return it.colours.some(function (c) { return c.fams.indexOf(f) > -1; }); }); });
    var brands = uniq(D.map(function (it) { return it.v; })).filter(Boolean).sort(function (a, b) { return a.localeCompare(b); });
    var hasWp = D.some(function (it) { return it.wp === true; }) && D.some(function (it) { return it.wp === false; });

    var results = D.filter(function (it) { return pass(it); });
    var nAll = 0, nFits = 0;
    D.forEach(function (it) { if (pass(it, "fit")) { nAll++; if (!hiddenByFit(it)) nFits++; } });

    var sortV = S.sort === "auto" ? (fa ? "fit" : "featured") : S.sort;
    if (sortV === "fit" && !fa) sortV = "featured";
    var GR = {};
    PROT_ORDER.forEach(function (p, i) { GR[p] = i; });
    function avail(it) { if (fa && it.fit && it.fit.vars.length) return it.fit.a ? 0 : 1; return it.av ? 0 : 1; }
    function tempRank(it) { return tempLive() && !it.tp ? 1 : 0; }
    results.sort(function (a, b) {
      switch (sortV) {
        case "plo": return a.p - b.p || a.i - b.i;
        case "phi": return b.p - a.p || a.i - b.i;
        case "new": return b.pub - a.pub || a.i - b.i;
        case "rated": return (b.rv * Math.min(b.rc, 20)) - (a.rv * Math.min(a.rc, 20)) || a.i - b.i;
        case "prot": return (a.prot ? GR[a.prot] : 99) - (b.prot ? GR[b.prot] : 99) || avail(a) - avail(b) || a.i - b.i;
        case "fit":
          var la = a.fit ? F.LEVEL_RANK[a.fit.level] : a.sc ? 8 : 9, lb = b.fit ? F.LEVEL_RANK[b.fit.level] : b.sc ? 8 : 9;
          return la - lb || avail(a) - avail(b) || tempRank(a) - tempRank(b) || (b.fit && a.fit ? b.fit.score - a.fit.score : 0) || a.i - b.i;
        default: return avail(a) - avail(b) || tempRank(a) - tempRank(b) || a.i - b.i;
      }
    });

    var pb = priceBounds();
    var pLo = S.pLo == null ? pb.min : S.pLo, pHi = S.pHi == null ? pb.max : S.pHi;
    var priceOn = pLo > pb.min || pHi < pb.max;

    var LBL = {
      cat: function (v) { return CAT_LABEL[v]; }, gen: function (v) { return GEN_LABEL[v]; },
      size: function (v) { return chipKeys[v] ? chipKeys[v].v : v.split(":")[1]; }, len: function (v) { return v + " length"; },
      prot: function (v) { return v === "None" ? "No CE rating" : v; }, wp: function (v) { return v; },
      rs: function (v) { return v; }, col: function (v) { return v; }, brand: function (v) { return v; }
    };
    var applied = [];
    if (fa && S.fitsOnly) applied.push({ label: "Fits my measurements", act: "fits-off" });
    if (tempLive()) applied.push({ label: "Rides " + tempText(), act: "temp-off" });
    Object.keys(S.sel).forEach(function (k) { S.sel[k].forEach(function (v) { applied.push({ label: LBL[k](v), act: "toggle", k: k, v: v }); }); });
    if (priceOn) applied.push({ label: money(pLo * 100) + "–" + money(pHi * 100), act: "price-off" });
    if (S.inStock) applied.push({ label: fa ? "Your size in stock" : "In stock", act: "stock-off" });

    var suggestions = [];
    if (!results.length) {
      var tries = [];
      if (fa && S.fitsOnly) tries.push(["Show closest sizes too", { fitsOnly: false }]);
      if (tempLive()) tries.push(["Any temperature", { tempOn: false }]);
      var NM = { cat: "category", gen: "gender", size: "size", len: "length", prot: "protection", wp: "waterproof", rs: "riding style", col: "colour", brand: "brand" };
      Object.keys(S.sel).forEach(function (k) { if (S.sel[k].length) { var sel = {}; Object.keys(S.sel).forEach(function (j) { sel[j] = j === k ? [] : S.sel[j]; }); tries.push(["Clear " + NM[k], { sel: sel }]); } });
      if (priceOn) tries.push(["Any price", { pLo: null, pHi: null }]);
      if (S.inStock) tries.push(["Include sold out", { inStock: false }]);
      tries.forEach(function (t, i) {
        var st = Object.assign({}, S, t[1]);
        var p2 = mkPass(st);
        var n = D.filter(function (it) { return p2(it); }).length;
        if (n) suggestions.push({ label: t[0] + " → " + n + " " + pluralNoun(n), i: i, n: n });
      });
      suggestions.sort(function (a, b) { return b.n - a.n; });
      S._tries = tries;
    }

    var bins = 12, step = Math.max(1, (pb.max - pb.min) / bins), hb = [];
    for (var b = 0; b < bins; b++) hb.push(0);
    D.forEach(function (it) { if (pass(it, "price")) hb[Math.min(bins - 1, Math.floor((it.p / 100 - pb.min) / step))]++; });
    var hmax = Math.max.apply(null, [1].concat(hb));
    var hist = hb.map(function (n, i) { var lo = pb.min + i * step, hi = lo + step; return { h: Math.round(n / hmax * 100), on: hi > pLo && lo < pHi }; });

    var sizeSummary = "";
    if (fa) {
      var anyFit = D.some(function (it) { return pass(it, "size") && fits(it) && it.fit.vars.length; });
      // What's been matched, named by the kinds of gear on show.
      var kinds = uniq(D.filter(function (it) { return it.fit; }).map(function (it) { return CAT_NOUN[it.cat] || "accessory"; }));
      var what = kinds.length && kinds.length <= 2 ? kinds.join(" and ") : "gear";
      sizeSummary = anyFit ? "We've already matched your measurements to the " + what + " selection." : "Nothing here matches your measurements yet. Check them, or show everything.";
    }

    return {
      results: results, nAll: nAll, nFits: nFits, sortV: sortV, fa: fa, applied: applied, suggestions: suggestions, hist: hist,
      pb: pb, pLo: pLo, pHi: pHi, priceOn: priceOn, sizeSummary: sizeSummary,
      catOpts: cats.length > 1 ? opts("cat", cats, LBL.cat) : [],
      genOpts: gens.length ? opts("gen", gens, LBL.gen) : [],
      sizeOpts: opts("size", sizeVals, LBL.size).map(function (o) { o.sys = chipKeys[o.v].sys; return o; }),
      lenOpts: lens.length > 1 ? opts("len", lens) : [],
      protOpts: prots.length ? opts("prot", prots, LBL.prot) : [],
      wpOpts: hasWp ? opts("wp", ["Waterproof", "Not waterproof"]) : [],
      rsOpts: rsVals.length ? opts("rs", rsVals) : [],
      colOpts: fams.length > 1 ? opts("col", fams).map(function (o) { o.bg = FAMILY_HEX[o.v]; return o; }) : [],
      brandOpts: brands.length > 1 ? opts("brand", brands) : [],
      hasTemp: D.some(function (it) { return it.tp; }),
      hasProt: prots.length > 0
    };
  }

  var SLIDERS = '<svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M3 5h9M16 5h1M3 10h3M10 10h7M3 15h9M16 15h1"/><circle cx="14" cy="5" r="2"/><circle cx="8" cy="10" r="2"/><circle cx="14" cy="15" r="2"/></svg>';
  var X_ICON = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.8"/></svg>';
  var TAPE = '<svg width="16" height="10" viewBox="0 0 20 12" aria-hidden="true"><rect x=".5" y=".5" width="19" height="11" fill="none" stroke="currentColor"/><path d="M4 1v4M7.5 1v6M11 1v4M14.5 1v6" stroke="currentColor"/></svg>';

  function measSummary() {
    var act = fieldsActive();
    if (!act.length) return "";
    var shown = act.slice(0, 3).map(function (k) { return FIELD[k][0] + " " + F.measValue(profile.meas[k]); }).join(" · ") + " cm";
    return act.length > 3 ? shown + " +" + (act.length - 3) + " more" : shown;
  }

  function renderFitbar(view) {
    var fields = collectionFields();
    var showMeas = fields.length > 0;
    var act = fieldsActive();
    var html = "";
    if (showMeas && barOff) {
      html = '<div class="pmo-fitoff">' + TAPE + "<span>Add your measurements to see your size on every product.</span>" +
        '<button type="button" class="pmo-linkbtn" data-act="bar-on">Show fit tools</button></div>';
    } else if (showMeas) {
      html += '<div class="pmo-fitbar' + (S.fitOpen ? " is-open" : "") + '">';
      html += '<span class="pmo-fb-label">Your fit</span>';
      html += '<div class="pmo-fb-group pmo-fb-meas"><span class="pmo-fb-k">Your measurements</span>' +
          '<button type="button" class="pmo-measbtn ' + (act.length ? "is-set" : "is-add") + '" data-act="toggle-fit" aria-expanded="' + S.fitOpen + '">' + TAPE +
          "<span>" + esc(act.length ? measSummary() : "Add your measurements") + "</span></button></div>";
      if (view.fa) {
        html += '<div class="pmo-fb-group"><span class="pmo-fb-k">Show</span><div class="pmo-seg" role="group" aria-label="Show">' +
          '<button type="button" class="' + (S.fitsOnly ? "is-on" : "") + '" aria-pressed="' + S.fitsOnly + '" data-act="fits-on">Fits me (' + view.nFits + ")</button>" +
          '<button type="button" class="' + (S.fitsOnly ? "" : "is-on") + '" aria-pressed="' + !S.fitsOnly + '" data-act="fits-off">Everything (' + view.nAll + ")</button></div></div>";
      } else if (S.fitLoading) {
        html += '<span class="pmo-note">Matching your size…</span>';
      }
      // Availability sits beside "Show" on desktop; phones keep it in the filters.
      html += '<div class="pmo-fb-group pmo-fb-stock"><span class="pmo-fb-k">Availability</span><div class="pmo-seg" role="group" aria-label="Availability">' +
        '<button type="button" class="' + (S.inStock ? "" : "is-on") + '" aria-pressed="' + !S.inStock + '" data-act="stock-off">All</button>' +
        '<button type="button" class="' + (S.inStock ? "is-on" : "") + '" aria-pressed="' + S.inStock + '" data-act="stock-on">' + (view.fa ? "My size in stock" : "In stock") + "</button></div></div>";
      html += '<button type="button" class="pmo-linkbtn pmo-fithide" data-act="bar-off">Hide</button>';
      if (S.fitOpen) html += fitPanelHtml(fields, act);
      html += "</div>";
      var mtext = [];
      if (act.length) mtext.push(measSummary());
      if (S.sel.gen.length) mtext.push(S.sel.gen.map(function (g) { return GEN_LABEL[g]; }).join(", "));
      // Opens the measurements sheet on phones.
      html += '<button type="button" class="pmo-mfit" data-act="toggle-fit"><span class="pmo-mfit-t"><span class="pmo-mk">Your fit</span><span class="pmo-mv">' +
        esc(mtext.length ? mtext.join(" · ") : "Add measurements to see your size on every product") +
        '</span></span><span class="pmo-linkbtn">' + (mtext.length ? "Edit" : "Add") + "</span></button>";
      html += '<button type="button" class="pmo-linkbtn pmo-mhide" data-act="bar-off">Hide fit tools</button>';
      // Clicking outside doesn't close the box: only its X (or Escape) does.
      html += '<div class="pmo-backdrop' + (S.fitOpen ? " is-on" : "") + '"></div>';
    }
    regions.fitbar.innerHTML = html;
    showTip();
  }

  function fitPanelHtml(fields, act) {
    var h = '<div class="pmo-fitpanel" role="dialog" aria-modal="true" aria-label="Your measurements">' +
      '<div class="pmo-fp-top"><div><p class="pmo-fp-h">Your measurements</p><p class="pmo-note">Take your own body measurements, and we\'ll match it against each product\'s size chart to give you a recommendation.</p></div>' +
      '<div class="pmo-fp-tr"><a class="pmo-fp-guide" href="' + esc(CFG.guideUrl || "/pages/how-to-take-measurements-for-motorcycle-gear") + '" target="_blank" rel="noopener">Full measuring guide' +
      '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 2h6v6M10 2 3 9"/></svg><span class="pmo-sr"> (opens in a new tab)</span></a>' +
      '<button type="button" class="pmo-close" aria-label="Close" data-act="toggle-fit">' + X_ICON + "</button></div></div>";
    var UI = window.PmoMeasureUI;
    var tipK = fields.indexOf(S.tipField) > -1 ? S.tipField : null;
    var areas = figAreas(fields, tipK);
    h += '<div class="pmo-fp-row pmo-fp-meas">' + (UI ? '<div class="pmo-fp-fig" data-g="' + figGroup(areas[0]) + '">' + UI.figureHtml(areas) + "</div>" : "") + '<div class="pmo-fp-inputs"><div class="pmo-fp-fields">';
    fields.forEach(function (k) {
      var v = profile.meas[k];
      h += '<label>' + esc(k === "palm" ? "Palm circumference" : FIELD[k][0]) + ' (cm)<input type="number" inputmode="decimal" step="0.5" min="5" max="250" placeholder="' + esc(FIELD[k][1]) + '" value="' + esc(v == null ? "" : v) + '" data-meas="' + esc(k) + '"></label>';
    });
    // How to take the measurement: shown while its box is in use.
    h += "</div>" + (act.length ? '<button type="button" class="pmo-linkbtn pmo-fp-clear" data-act="clear-fit">Clear measurements</button>' : "") + '<p class="pmo-fp-tip" data-pmo-tip aria-live="polite"' + (tipK ? "" : " hidden") + ">" + (tipK ? tipText(tipK) : "") + "</p></div></div>";
    h += '<div class="pmo-fp-actions"><div class="pmo-ack" role="group" aria-label="Before we show your fit"><label><input type="checkbox" data-ack' + (acked ? " checked" : "") + '><span>I understand suggested sizes are a guide based on each brand\'s own size chart, not personal fitting advice from Peak Moto.</span></label>' +
      '<p>For personalised fitting advice, <a href="' + CONSULT_URL + '">book a free fit consultation</a>. Your measurements are saved in this browser, so every collection shows your size.</p></div>' +
      '<button type="button" class="pmo-btn" data-act="toggle-fit"' + (acked ? "" : " disabled") + '>Show my fit</button></div></div>';
    return h;
  }

  // Shows the tip for the box in use and picks out its band on the figure,
  // without redrawing the box (so typing isn't interrupted).
  function showTip() {
    var p = rootEl.querySelector(".pmo-fitpanel");
    if (!p) return;
    var fields = collectionFields();
    var k = fields.indexOf(S.tipField) > -1 ? S.tipField : null;
    var box = p.querySelector("[data-pmo-tip]");
    if (box) { box.innerHTML = k ? tipText(k) : ""; box.hidden = !k; }
    var wrap = p.querySelector(".pmo-fp-fig");
    if (wrap && window.PmoMeasureUI) {
      var areas = figAreas(fields, k);
      var g = String(figGroup(areas[0]));
      if (wrap.getAttribute("data-g") !== g) { wrap.innerHTML = window.PmoMeasureUI.figureHtml(areas); wrap.setAttribute("data-g", g); }
    }
    p.querySelectorAll(".pmo-sg__band").forEach(function (b) { b.classList.toggle("is-on", !!k && b.getAttribute("data-area") === k); });
  }

  function secHtml(key, title, count, body, defOpen) {
    var open = S.open[key] != null ? S.open[key] : defOpen;
    return '<div class="pmo-sec"><button type="button" class="pmo-sec-h" data-act="sec" data-k="' + key + '" aria-expanded="' + open + '"><span>' + esc(title) +
      (count ? '<span class="pmo-cnt">' + count + "</span>" : "") + '</span><span aria-hidden="true">' + (open ? "–" : "+") + "</span></button>" +
      (open ? '<div class="pmo-sec-b">' + body + "</div>" : "") + "</div>";
  }
  function checkList(list) {
    return list.map(function (o) {
      return '<label class="pmo-opt' + (o.dis ? " is-dis" : "") + '"><input type="checkbox" data-act="toggle" data-k="' + o.k + '" data-v="' + esc(o.v) + '"' + (o.on ? " checked" : "") + (o.dis ? " disabled" : "") + ">" +
        "<span>" + esc(o.label) + '</span><span class="pmo-n">' + o.n + "</span></label>";
    }).join("");
  }
  function cnt(k) { return S.sel[k].length ? String(S.sel[k].length) : ""; }

  var SYS_LABEL = { L: "Letter sizes", AU: "AU sizes", EU: "EU sizes", W: "Waist (inches)", US: "US sizes", CM: "Head (cm)", O: "Other" };

  function renderRail(view) {
    var h = '<div class="pmo-rail-head"><span>Filter &amp; sort</span><button type="button" class="pmo-close" aria-label="Close filters" data-act="close-sheets">' + X_ICON + "</button></div><div class=\"pmo-rail-body\">";
    var sorts = sortList(view);
    h += '<div class="pmo-sec pmo-sortm"><div class="pmo-sec-h">Sort</div><div class="pmo-sec-b">' + sorts.map(function (s) {
      return '<label class="pmo-opt"><input type="radio" name="pmo-msort" data-act="sort" data-v="' + s[0] + '"' + (view.sortV === s[0] ? " checked" : "") + "><span>" + esc(s[1]) + "</span></label>";
    }).join("") + "</div></div>";

    if (view.catOpts.length) h += secHtml("cat", "Category", cnt("cat"), checkList(view.catOpts), true);
    if (view.genOpts.length) h += secHtml("gen", "Gender", cnt("gen"), '<p class="pmo-note">Women\'s and Men\'s include unisex gear.</p>' + checkList(view.genOpts), true);

    if (view.sizeOpts.length) {
      var groups = uniq(view.sizeOpts.map(function (o) { return o.sys; }));
      var body = view.fa ? '<div class="pmo-fitsum">' + esc(view.sizeSummary) + "</div>" : "";
      groups.forEach(function (g) {
        if (groups.length > 1) body += '<p class="pmo-chips-h">' + esc(SYS_LABEL[g]) + "</p>";
        body += '<div class="pmo-chips">' + view.sizeOpts.filter(function (o) { return o.sys === g; }).map(function (o) {
          return '<button type="button" class="pmo-chip' + (o.on ? " is-on" : "") + (o.dis ? " is-dis" : "") + '" data-act="toggle" data-k="size" data-v="' + esc(o.v) + '" aria-pressed="' + o.on + '"' + (o.dis ? " disabled" : "") + ">" + esc(o.label) + "<small>" + o.n + "</small></button>";
        }).join("") + "</div>";
      });
      h += secHtml("size", "Size", cnt("size"), body, view.fa || view.catOpts.length < 2 || S.sel.cat.length > 0);
    }
    if (view.lenOpts.length) h += secHtml("len", "Length", cnt("len"), '<p class="pmo-note">Short includes Petite. Long includes Tall.</p>' + checkList(view.lenOpts), true);
    if (view.protOpts.length) h += secHtml("prot", "Protection", cnt("prot"), (view.protOpts.some(function (o) { return /^CE (A|B|C)/.test(o.v); }) ? '<p class="pmo-note">CE garment ratings run AAA (highest) to C.</p>' : "") + checkList(view.protOpts), true);
    // Riding temperature sits with the filters, above Waterproof.
    if (view.hasTemp) {
      h += secHtml("temp", "Riding temperature", S.tempOn ? "1" : "", '<p class="pmo-note">The temperatures you ride in. Shows gear made for any of them.</p><div class="pmo-temp">' +
        '<span class="pmo-tv" data-pmo-tv>' + esc(tempText()) + "</span>" +
        (S.tempOn ? '<button type="button" class="pmo-linkbtn" data-act="temp-off">Any</button>' : "") +
        '<div class="pmo-dual' + (S.tempOn ? "" : " is-off") + '" data-pmo-tdual><div class="pmo-trk"></div><div class="pmo-fill" data-pmo-tfill style="left:' + (S.tLo / 35 * 100).toFixed(1) + "%;width:" + ((S.tHi - S.tLo) / 35 * 100).toFixed(1) + '%"></div>' +
        '<input type="range" min="0" max="35" step="1" value="' + S.tLo + '" data-range="tLo" aria-label="Coldest temperature you ride in">' +
        '<input type="range" min="0" max="35" step="1" value="' + S.tHi + '" data-range="tHi" aria-label="Warmest temperature you ride in"></div></div>', true);
    }
    if (view.wpOpts.length) h += secHtml("wp", "Waterproof", cnt("wp"), checkList(view.wpOpts), true);
    if (view.rsOpts.length) h += secHtml("rs", "Riding style", cnt("rs"), checkList(view.rsOpts), false);
    if (view.colOpts.length) {
      h += secHtml("col", "Colour", cnt("col"), '<div class="pmo-cols">' + view.colOpts.map(function (o) {
        return '<button type="button" class="pmo-colopt' + (o.on ? " is-on" : "") + (o.dis ? " is-dis" : "") + '" data-act="toggle" data-k="col" data-v="' + esc(o.v) + '" aria-pressed="' + o.on + '"' + (o.dis ? " disabled" : "") + '><span class="pmo-dot" style="background:' + o.bg + '"></span><span>' + esc(o.label) + '</span><span class="pmo-n">' + o.n + "</span></button>";
      }).join("") + "</div>", true);
    }
    var pb = view.pb;
    var span = pb.max - pb.min || 1;
    h += secHtml("price", "Price", view.priceOn ? "1" : "", '<div class="pmo-hist" aria-hidden="true">' + view.hist.map(function (b) { return '<span class="' + (b.on ? "is-in" : "") + '" style="height:' + b.h + '%"></span>'; }).join("") + "</div>" +
      '<div class="pmo-dual"><div class="pmo-trk"></div><div class="pmo-fill" data-pmo-pfill style="left:' + ((view.pLo - pb.min) / span * 100).toFixed(1) + "%;width:" + ((view.pHi - view.pLo) / span * 100).toFixed(1) + '%"></div>' +
      '<input type="range" min="' + pb.min + '" max="' + pb.max + '" step="10" value="' + view.pLo + '" data-range="pLo" aria-label="Minimum price">' +
      '<input type="range" min="' + pb.min + '" max="' + pb.max + '" step="10" value="' + view.pHi + '" data-range="pHi" aria-label="Maximum price"></div>' +
      '<div class="pmo-pricev"><span data-pmo-plo>' + money(view.pLo * 100) + "</span><span data-pmo-phi>" + money(view.pHi * 100) + "</span></div>", true);
    // Brands with nothing left to show drop out of the list.
    var brandsLeft = view.brandOpts.filter(function (o) { return !o.dis; });
    if (brandsLeft.length) h += secHtml("brand", "Brand", cnt("brand"), checkList(brandsLeft), brandsLeft.length <= 8);
    var stockUp = !barOff && collectionFields().length > 0;
    h += secHtml("stock", "Availability", S.inStock ? "1" : "", '<label class="pmo-switch"><input type="checkbox" data-act="stock"' + (S.inStock ? " checked" : "") + '><span><span class="pmo-st">' +
      (view.fa ? "Your size in stock" : "In stock now") + '</span><span class="pmo-ss">' + (view.fa ? "Hides products where the size that fits you is sold out." : "Hides products that are sold out in every size.") + "</span></span></label>", true)
      .replace('class="pmo-sec"', 'class="pmo-sec' + (stockUp ? " pmo-desk-hide" : "") + '"');
    h += '</div><div class="pmo-rail-foot"><button type="button" class="pmo-btn pmo-btn--ghost" data-act="clear-all">Clear all</button><button type="button" class="pmo-btn" data-act="close-sheets">Show ' + view.results.length + " results</button></div>";
    var scroll = regions.rail.querySelector(".pmo-rail-body");
    var top = scroll ? scroll.scrollTop : 0;
    regions.rail.innerHTML = h;
    regions.rail.classList.toggle("is-open", S.sheet === "filters");
    var nb = regions.rail.querySelector(".pmo-rail-body");
    if (nb) nb.scrollTop = top;
  }

  function sortList(view) {
    return [["fit", "Best fit for me"], ["featured", "Featured"], ["new", "Newest"], ["plo", "Price, low to high"], ["phi", "Price, high to low"], ["rated", "Top rated"], ["prot", "Most protective"]]
      .filter(function (s) { return (s[0] !== "fit" || view.fa) && (s[0] !== "prot" || view.hasProt); });
  }

  var STAR = "M7 1l1.8 3.9 4.2.5-3.1 2.9.8 4.2L7 10.4 3.3 12.5l.8-4.2L1 5.4l4.2-.5z";
  function starsHtml(rating) {
    var pct = Math.max(0, Math.min(100, rating / 5 * 100));
    var paths = [0, 16, 32, 48, 64].map(function (x) { return '<path transform="translate(' + x + ' 0)" d="' + STAR + '"/>'; }).join("");
    return '<span class="pmo-stars" aria-hidden="true"><svg class="pmo-stars-bg" viewBox="0 0 80 14">' + paths + '</svg><span class="pmo-stars-fg" style="width:' + pct.toFixed(0) + '%"><svg viewBox="0 0 80 14">' + paths + "</svg></span></span>";
  }

  var LEVEL = { great: ["Suggested size", "pmo-lv-great"], good: ["Suggested size", "pmo-lv-good"], close: ["Closest size", "pmo-lv-close"], out: ["Outside the size range", "pmo-lv-out"] };

  // Short form of the product page's "You sit between sizes" note: the
  // sizes either side, and why the suggested one was picked.
  function spanSizes(it, f) {
    var x = F.splitLength(f.label);
    var len = x ? " " + x.length : "";
    function text(label) {
      var vs = F.variantsFor(it, label + len);
      var v0 = vs.filter(function (v) { return v.a; })[0] || vs[0];
      return v0 ? sizeTextOf(it, v0).replace(" · ", " ") : label + len;
    }
    // Each size on its own line, joined by a light "to" / "or" so the two
    // read as separate sizes. Short sizes ("XS or S") share one line.
    var a = text(f.span.from), b = text(f.span.to), join = f.span.wide ? "to" : "or";
    if (a.length <= 4 && b.length <= 4) return '<span class="pmo-sz pmo-sz--row">' + esc(a) + '<span class="pmo-or"> ' + join + " </span>" + esc(b) + "</span>";
    return '<span class="pmo-sz">' + esc(a) + '</span><span class="pmo-sz"><span class="pmo-or">' + join + "</span> " + esc(b) + "</span>";
  }
  function spanSentence(sp) {
    // Kept short: what the size was picked for, then where it may not sit
    // just right ("Sized for your waist. May be roomy in the hips.").
    var why = sp.fits.slice();
    if (sp.legFits) why.push("leg length");
    var after = [];
    // Gloves: hand length reads as long or short, not roomy or snug.
    var notHand = function (a) { return a !== "hand length"; };
    var roomy = sp.roomy.filter(notHand), snug = sp.snug.filter(notHand);
    if (roomy.length) after.push("roomy in the " + listText(roomy));
    if (snug.length) after.push("snug in the " + listText(snug));
    if (sp.roomy.length > roomy.length) after.push("long in the fingers");
    if (sp.snug.length > snug.length) after.push("short in the fingers");
    if (sp.long) after.push("long in the leg");
    if (sp.short) after.push("short in the leg");
    var out = why.length ? "Sized for your " + listText(why) + "." : "";
    if (after.length) out += (out ? " " : "") + "May be " + listText(after) + ".";
    return out;
  }

  function fitSentence(f) {
    if (f.span && (f.level === "great" || f.level === "good")) return spanSentence(f.span);
    var worst = -1, wd = 0;
    f.positions.forEach(function (p, i) { var d = Math.abs(p - 50); if (d > wd) { wd = d; worst = i; } });
    var areas = uniq(f.areas);
    if (f.level === "great" || worst < 0 || wd <= 33) return "Fits your " + listText(areas) + " well.";
    var p = f.positions[worst], a = f.areas[worst];
    if (f.areas[worst] === "inseam" || f.areas[worst] === "height") {
      var w = f.areas[worst] === "inseam" ? "at the inseam" : "in the leg";
      if (p <= 10) return "Short " + w + ". Worth trying on.";
      if (p < 50) return "A little short " + w + "." + (f.level === "good" ? "" : " Worth trying on.");
      if (p >= 92) return "Long " + w + ". Worth trying on.";
      return "A little long " + w + "." + (f.level === "good" ? "" : " Worth trying on.");
    }
    if (f.areas[worst] === "hand length") {
      var gl = p < 50 ? "Glove length may be too short." : "Glove length may be too long.";
      return gl + (p <= 10 || p >= 92 || f.level !== "good" ? " Worth trying on." : "");
    }
    if (p <= 10) return "Tight at the " + a + ". Worth trying on.";
    if (p < 50) return "A little snug at the " + a + "." + (f.level === "good" ? "" : " Worth trying on.");
    if (p >= 92) return "Loose at the " + a + ". Worth trying on.";
    return "A little roomy at the " + a + "." + (f.level === "good" ? "" : " Worth trying on.");
  }

  // Charts that size by too few measurements can't promise a fit: pants
  // without both waist and hips, jackets with a single body measurement.
  var THIN_WORD = { hips: "hip", waist: "waist", inseam: "inseam", chest: "chest", thigh: "thigh", height: "height" };
  var AREA_SEQ = ["chest", "waist", "hips", "thigh", "inseam", "height"];
  function thinChartNote(it) {
    if (it.cat !== "pants" && it.cat !== "jackets") return "";
    var charts = S.fit && S.fit[it.h];
    if (!charts || !charts.length) return "";
    var seen = {}, unknown = false;
    charts.forEach(function (c) {
      ((c.calc && c.calc.fields) || []).forEach(function (fl) {
        var k = F.areaKey(fl.label);
        if (k && THIN_WORD[k]) seen[k] = 1; else unknown = true;
      });
    });
    var areas = AREA_SEQ.filter(function (k) { return seen[k]; });
    if (!areas.length) return "";
    var thin;
    if (it.cat === "pants") thin = !(seen.waist && seen.hips);
    else thin = !unknown && areas.filter(function (k) { return k !== "height" && k !== "inseam"; }).length === 1;
    if (!thin) return "";
    return '<span class="pmo-est">Sized by ' + listText(areas.map(function (k) { return THIN_WORD[k]; })) + ' only. <a href="' + CONSULT_URL + '">Book a fit consult</a> to double check sizing.</span>';
  }

  // Why a product with measurements in has no size: no chart yet, a chart of
  // the garment's own measurements (linked straight to the product page's
  // size guide), or a chart that needs a measurement not given yet.
  var GARMENT_RE = /\bflat\b|width|armpit|\bof (a )?(jacket|garment|hoodie|tee|shirt|pants|jeans)\b/i;
  function noMatchHtml(it) {
    var none = "No size chart to match yet. See the product page for sizing.";
    var charts = S.fit && S.fit[it.h];
    if (!charts || !charts.length) return esc(none);
    var labels = [];
    charts.forEach(function (c) { ((c.calc && c.calc.fields) || []).forEach(function (fl) { labels.push(String(fl.label || "")); }); });
    if (!labels.length) return esc(none);
    if (labels.every(function (l) { return GARMENT_RE.test(l) && !/palm|hand|head|foot/i.test(l); })) {
      return "This product is sized by the garment, not by body measurements. " +
        '<a class="pmo-sglink" style="text-decoration:underline;text-underline-offset:2px" href="' + esc(it.u.replace(/#.*$/, "") + "#size-guide") + '">Click here for size chart</a>';
    }
    var can = collectionFields();
    var need = uniq(labels.map(F.areaKey).filter(function (k) { return k && can.indexOf(k) > -1 && F.measValue(profile.meas[k]) == null; }));
    if (need.length) return esc("Add your " + listText(need.map(function (k) { return k === "palm" ? "palm circumference" : FIELD[k][0].toLowerCase(); })) + " to see your size.");
    return esc(none);
  }

  // With measurements in, the product link opens on the suggested size: in
  // the colour the product leads with, else a colour that's in stock.
  function cardUrl(it, view) {
    var f = it.fit;
    if (!view.fa || !f || f.missing || !f.vars || !f.vars.length) return it.u;
    var col = it.colIdx > -1 && it.vars[0] ? it.vars[0].o[it.colIdx] : null;
    var same = function (v) { return col == null || v.o[it.colIdx] === col; };
    var v = f.vars.filter(function (x) { return x.a && same(x); })[0] || f.vars.filter(function (x) { return x.a; })[0] || f.vars.filter(same)[0] || f.vars[0];
    if (!v || !v.id) return it.u;
    return it.u + (it.u.indexOf("?") > -1 ? "&" : "?") + "variant=" + v.id;
  }

  function cardHtml(it, view) {
    var url = cardUrl(it, view);
    var h = '<article class="pmo-card">';
    h += '<a class="pmo-ph" href="' + esc(url) + '" tabindex="-1" aria-hidden="true">';
    if (it.img) h += '<img src="' + esc(it.img) + '" alt="" loading="lazy" width="600" height="750" crossorigin="anonymous"' + (LIFT[it.img] ? ' style="filter:brightness(' + LIFT[it.img] + ')"' : "") + ">";
    if (it.av && it.cp > it.p) {
      // The biggest saving across sizes; "Up to" when prices vary.
      var off = Math.round((it.cp - it.p) / it.cp * 100);
      h += '<span class="pmo-flag pmo-flag--sale">' + (off >= 1 ? (it.pm > it.p ? "Up to " : "") + off + "% off" : "Sale") + "</span>";
    }
    // Final sale items (named in the title) are flagged opposite the saving.
    if (/final sale/i.test(it.t)) h += '<span class="pmo-flag pmo-flag--final">Final sale</span>';
    h += "</a>";
    // Boots and shoes show whether they're waterproof instead of their CE line.
    var pill = it.cat === "boots" ? (it.wp === true ? "Waterproof" : null) : it.noCE ? null : it.prot;
    h += '<div class="pmo-brow"><span class="pmo-brand">' + esc(it.v) + "</span>" + (pill ? '<span class="pmo-ce">' + esc(pill) + "</span>" : "") + "</div>";
    if (it.noCE) h += '<span class="pmo-cenote">Australian brand, CE not required</span>';
    h += '<a class="pmo-title" href="' + esc(url) + '">' + esc(it.t) + "</a>";
    h += '<div class="pmo-rev" aria-label="' + (it.rc ? "Rated " + it.rv.toFixed(1) + " out of 5 from " + it.rc + " reviews" : "No reviews yet") + '">' + starsHtml(it.rv) + '<span class="pmo-revn">(' + it.rc + ")</span></div>";
    var rt = tempLive() && it.tp ? rangeText(it.tp) : "";
    if (rt) h += '<div class="pmo-ctemp" title="Suggested riding temperature">' + THERMO + "<span>" + esc(rt) + "</span></div>";
    var cols = it.colours.slice();
    if (S.sel.col.length) cols.sort(function (a, b) { return (b.fams.some(function (f) { return S.sel.col.indexOf(f) > -1; }) ? 1 : 0) - (a.fams.some(function (f) { return S.sel.col.indexOf(f) > -1; }) ? 1 : 0); });
    h += '<div class="pmo-prow"><span class="pmo-price">' + (it.pm > it.p ? "From " : "") + money(it.p) + (it.cp > it.p ? ' <s class="pmo-was">' + money(it.cp) + "</s>" : "") + "</span>";
    if (cols.length) {
      h += '<span class="pmo-sws" aria-label="' + esc(cols.length + (cols.length === 1 ? " colour: " : " colours: ") + cols.map(function (c) { return c.n; }).join(", ")) + '">' +
        // Up to five swatches; past that, four and a count.
        cols.slice(0, cols.length > 5 ? 4 : 5).map(function (c) { return '<span class="pmo-sw" style="background:' + c.bg + '" title="' + esc(c.n) + '"></span>'; }).join("") +
        (cols.length > 5 ? '<span class="pmo-swm">+' + (cols.length - 4) + "</span>" : "") + "</span>";
    }
    h += "</div>";
    var f = it.fit;
    if (view.fa && f && f.missing) {
      h += '<div class="pmo-fitbox"><span class="pmo-lvl pmo-lv-out"><span class="pmo-lvl-k"><i></i>None left in your size</span></span>' +
        '<span class="pmo-sent">' + esc("Your size here is " + f.missing + ", which is no longer available in this style.") + "</span></div>";
    } else if (view.fa && f) {
      var lv = LEVEL[f.level];
      // Between sizes: both sizes, as the product page's size guide says.
      // Pants lead with the one size picked; the note says where it may not
      // sit just right.
      var between = f.span && (f.level === "great" || f.level === "good") && it.cat !== "pants";
      // Laid out like Peak Promise's "Online / Ready to Dispatch" row: a light
      // label with its dot, and the size in bold on the right.
      h += '<div class="pmo-fitbox"><span class="pmo-lvl ' + lv[1] + '"><span class="pmo-lvl-k"><i></i>' + esc(between ? "Suggested sizes" : lv[0]) + '</span><span class="pmo-lvl-v">' + (f.level === "out" ? "closest " : "") + (between ? spanSizes(it, f) : esc(f.sizeText)) + "</span></span>" +
        '<span class="pmo-sent">' + esc(fitSentence(f)) + "</span>" +
        thinChartNote(it) +
        (it.est ? '<span class="pmo-est">Estimate from the brand\'s garment measurements. Treat it as a guide.</span>' : "") + "</div>";
    } else if (view.fa && it.cat !== "other" && it.cat !== "protection") {
      h += '<div class="pmo-fitbox"><span class="pmo-sent">' + noMatchHtml(it) + "</span></div>";
    }
    if (!(view.fa && f)) {
      var chips = Object.keys(it.chips).map(function (k) { return it.chips[k]; });
      var primary = chips.length ? chips.filter(function (c) { return c.sys === chips.slice().sort(function (a, b) { return F.chipRank(a) - F.chipRank(b); })[0].sys; }) : [];
      primary.sort(function (a, b) { return F.chipRank(a) - F.chipRank(b); });
      var nLen = Object.keys(it.lens).length;
      if (primary.length) {
        h += '<span class="pmo-szsum">' + esc((primary.length > 1 ? "Sizes " + primary[0].v + "–" + primary[primary.length - 1].v : "Size " + primary[0].v) +
          (nLen > 1 ? " · " + nLen + " lengths" : "")) + "</span>";
      }
    }
    return h + "</article>";
  }

  // What the size suggestions can't see, for the kinds of gear on show, and
  // where to get a proper fitting.
  function fitNoteHtml(list) {
    if (noteOff) return "";
    var cats = uniq(list.filter(function (it) { return it.fit; }).map(function (it) { return it.cat; }));
    var bits = [];
    if (cats.indexOf("boots") > -1) bits.push("Boot size charts only go by foot length, so if you have wide feet or a high instep you may need a size up.");
    if (cats.indexOf("gloves") > -1) bits.push("Glove size charts don't account for finger length, so long or short fingers may suit a different size.");
    bits.push("Sizes are suggested from each brand's own size chart, so treat them as a guide.");
    var box = infoBox(esc(bits.join(" ")) + ' For personalised fitting advice, <a href="' + CONSULT_URL + '">book a free fit consultation</a>.', "pmo-fitnote");
    return box.slice(0, -6) + '<button type="button" class="pmo-note-x" data-act="hide-fitnote">Don\'t show again<svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M2.5 2.5l7 7M9.5 2.5l-7 7"/></svg></button></div>';
  }

  function renderResults(view) {
    var r = view.results;
    var h = '<div class="pmo-topbar"><p class="pmo-count"><b>' + r.length + " " + esc(pluralNoun(r.length)) + "</b> " +
      esc(view.fa && S.fitsOnly ? "fit your measurements" : view.fa ? "with your closest size shown" : "in this collection") + "</p>" +
      // Phones open the filters from here, beside the count.
      '<button type="button" class="pmo-mfilter" data-act="open-filters" aria-label="Filter and sort' + (view.applied.length ? ", " + view.applied.length + " applied" : "") + '">' + SLIDERS + "<span>Filter</span>" +
      (view.applied.length ? '<span class="pmo-mfilter-n">' + view.applied.length + "</span>" : "") + "</button>" +
      '<label class="pmo-sortl">Sort by <select data-sort>' + sortList(view).map(function (s) { return '<option value="' + s[0] + '"' + (view.sortV === s[0] ? " selected" : "") + ">" + esc(s[1]) + "</option>"; }).join("") + "</select></label></div>";
    if (view.fa) h += fitNoteHtml(r);
    if (view.applied.length) {
      h += '<div class="pmo-applied">' + view.applied.map(function (a) {
        return '<button type="button" class="pmo-ac" data-act="' + a.act + '"' + (a.k ? ' data-k="' + a.k + '" data-v="' + esc(a.v) + '"' : "") + ' aria-label="Remove ' + esc(a.label) + '">' + esc(a.label) + X_ICON + "</button>";
      }).join("") + '<button type="button" class="pmo-linkbtn" data-act="clear-all">Clear all</button></div>';
    }
    if (!r.length) {
      h += '<div class="pmo-empty"><h3>Nothing here matches all of that</h3><p class="pmo-note">Loosen one thing and you\'ll have options again:</p><div class="pmo-sugg">' +
        view.suggestions.map(function (s) { return '<button type="button" class="pmo-btn pmo-btn--ghost" data-act="suggest" data-i="' + s.i + '">' + esc(s.label) + "</button>"; }).join("") + "</div></div>";
    }
    h += '<div class="pmo-grid">' + r.slice(0, S.shown).map(function (it) { return cardHtml(it, view); }).join("") + "</div>";
    // One line under the grid: what "Fits me" is hiding (the count shows
    // them), then how many are on screen, then the Show more button.
    var hidden = view.fa && S.fitsOnly ? view.nAll - view.nFits : 0;
    var more = r.length > S.shown;
    if (hidden || more) {
      var line = hidden ? '<button type="button" class="pmo-linkbtn" data-act="fits-off" style="font-size:inherit">' + hidden + " more " + esc(pluralNoun(hidden)) + "</button> come close to your measurements." : "";
      if (more) line += (hidden ? " Currently showing " : "Showing ") + Math.min(S.shown, r.length) + " of " + r.length;
      h += '<div class="pmo-more"><p class="pmo-note">' + line + "</p>" + (more ? '<button type="button" class="pmo-btn pmo-btn--ghost" data-act="more">Show more</button>' : "") + "</div>";
    }
    regions.results.innerHTML = h;
    regions.results.querySelectorAll(".pmo-ph img").forEach(function (img) {
      if (img.complete && img.naturalWidth) lift(img); else img.addEventListener("load", function () { lift(img); }, { once: true });
    });
  }

  // Some supplier photos sit on a flat light-grey backdrop instead of white,
  // which shows as a grey box on the tile. When a photo's edges are an even
  // light grey, brighten it just enough that the backdrop reads as white and
  // blends into the tile like the other photos.
  var LIFT = {};
  function lift(img) {
    var key = img.getAttribute("src");
    if (LIFT[key] == null) {
      LIFT[key] = 0;
      try {
        var n = 48, c = document.createElement("canvas");
        c.width = c.height = n;
        var x = c.getContext("2d");
        x.drawImage(img, 0, 0, n, n);
        // Sample down both side edges; a product or model touching an edge
        // only knocks out a few samples.
        var d = x.getImageData(0, 0, n, n).data, lo = 255, hi = 0, hits = 0;
        [1, n - 2].forEach(function (px) {
          [2, 10, 18, 26, 34, 42].forEach(function (py) {
            var i = (py * n + px) * 4, r = d[i], g = d[i + 1], b = d[i + 2], v = (r + g + b) / 3;
            if (Math.max(r, g, b) - Math.min(r, g, b) > 8 || v < 205 || v >= 252) return;
            hits++; lo = Math.min(lo, v); hi = Math.max(hi, v);
          });
        });
        if (hits >= 9 && lo < 250 && hi - lo <= 30) LIFT[key] = +(255 / lo).toFixed(3);
      } catch (e) {}
    }
    if (LIFT[key]) img.style.filter = "brightness(" + LIFT[key] + ")";
  }

  // ---------- events ----------

  function toggleSel(k, v) {
    var cur = S.sel[k];
    S.sel[k] = cur.indexOf(v) > -1 ? cur.filter(function (x) { return x !== v; }) : cur.concat([v]);
    if (k === "gen") saveRider();
    S.shown = PER_PAGE;
  }
  function lockScroll() {
    document.documentElement.classList.toggle("pmo-col-locked", !!S.sheet || S.fitOpen);
  }

  rootEl.addEventListener("click", function (e) {
    var el = e.target.closest("[data-act]");
    if (!el || !rootEl.contains(el)) return;
    var act = el.getAttribute("data-act");
    if (el.tagName === "INPUT") return; // handled on change
    var k = el.getAttribute("data-k"), v = el.getAttribute("data-v");
    switch (act) {
      case "toggle": toggleSel(k, v); break;
      case "sec": S.open[k] = el.getAttribute("aria-expanded") !== "true"; break;
      case "bar-off": setBarOff(true); S.fitOpen = false; break;
      case "bar-on": setBarOff(false); if (fieldsActive().length && !S.fit) loadFit(); break;
      case "hide-fitnote": noteOff = true; try { localStorage.setItem(NOTE_KEY, "1"); } catch (e) {} break;
      case "toggle-fit": S.fitOpen = !S.fitOpen; S.sheet = null; if (!S.fitOpen && fieldsActive().length) { S.fitsOnly = true; loadFit(); } break;
      case "close-sheets": S.fitOpen = false; S.sheet = null; break;
      case "open-filters": S.sheet = "filters"; S.fitOpen = false; break;
      case "fits-on": S.fitsOnly = true; S.shown = PER_PAGE; break;
      case "fits-off": S.fitsOnly = false; break;
      case "temp-off": S.tempOn = false; saveRider(); break;
      case "price-off": S.pLo = S.pHi = null; break;
      case "stock-off": S.inStock = false; break;
      case "stock-on": S.inStock = true; break;
      case "clear-fit": FIELD_ORDER.forEach(function (f) { delete profile.meas[f]; }); F.saveProfile(profile); computeFit(); break;
      case "clear-all":
        Object.keys(S.sel).forEach(function (j) { S.sel[j] = []; });
        S.pLo = S.pHi = null; S.inStock = false; S.tempOn = false; S.fitsOnly = false; saveRider(); break;
      case "suggest": var t = (S._tries || [])[+el.getAttribute("data-i")]; if (t) Object.assign(S, t[1]); if (t && (t[1].tempOn === false || t[1].sel)) saveRider(); break;
      case "more": S.shown += PER_PAGE; break;
      default: return;
    }
    lockScroll();
    render();
  });

  rootEl.addEventListener("change", function (e) {
    var el = e.target;
    var act = el.getAttribute("data-act");
    if (act === "toggle") { toggleSel(el.getAttribute("data-k"), el.getAttribute("data-v")); render(); return; }
    if (act === "stock") { S.inStock = el.checked; render(); return; }
    if (act === "sort") { S.sort = el.getAttribute("data-v"); render(); return; }
    if (el.hasAttribute("data-sort")) { S.sort = el.value; render(); return; }
    if (el.hasAttribute("data-ack")) { setAck(el.checked); if (fieldsActive().length) loadFit(); else render(); return; }
    if (el.hasAttribute("data-range")) { rangeDone(el); }
  });

  rootEl.addEventListener("focusin", function (e) {
    var k = e.target.getAttribute && e.target.getAttribute("data-meas");
    if (!k || S.tipField === k) return;
    S.tipField = k;
    showTip();
  });
  rootEl.addEventListener("focusout", function () {
    setTimeout(function () {
      var a = document.activeElement;
      if (a && a.hasAttribute && a.hasAttribute("data-meas") && rootEl.contains(a)) return;
      if (!S.tipField) return;
      S.tipField = null;
      showTip();
    }, 0);
  });

  var typing = null;
  rootEl.addEventListener("input", function (e) {
    var el = e.target;
    if (el.hasAttribute("data-meas")) {
      var k = el.getAttribute("data-meas");
      var v = F.measValue(el.value);
      if (v == null) delete profile.meas[k]; else profile.meas[k] = v;
      F.saveProfile(profile);
      clearTimeout(typing);
      typing = setTimeout(function () { S.fitsOnly = true; if (fieldsActive().length) loadFit(); else { computeFit(); render(); } }, 450);
      return;
    }
    if (el.hasAttribute("data-range")) rangeMove(el);
  });

  function rangeMove(el) {
    var key = el.getAttribute("data-range");
    var val = +el.value;
    if (key === "tLo" || key === "tHi") {
      if (key === "tLo") S.tLo = Math.min(val, S.tHi); else S.tHi = Math.max(val, S.tLo);
      el.value = key === "tLo" ? S.tLo : S.tHi;
      S.tempOn = true;
      var fill = rootEl.querySelector("[data-pmo-tfill]");
      if (fill) { fill.style.left = (S.tLo / 35 * 100).toFixed(1) + "%"; fill.style.width = ((S.tHi - S.tLo) / 35 * 100).toFixed(1) + "%"; }
      var tv = rootEl.querySelector("[data-pmo-tv]");
      if (tv) tv.textContent = tempText();
      var d = rootEl.querySelector("[data-pmo-tdual]");
      if (d) d.classList.remove("is-off");
    } else {
      var pb = priceBounds();
      var lo = S.pLo == null ? pb.min : S.pLo, hi = S.pHi == null ? pb.max : S.pHi;
      if (key === "pLo") lo = Math.min(val, hi); else hi = Math.max(val, lo);
      S.pLo = lo <= pb.min ? null : lo; S.pHi = hi >= pb.max ? null : hi;
      el.value = key === "pLo" ? lo : hi;
      var span = pb.max - pb.min || 1;
      var pf = rootEl.querySelector("[data-pmo-pfill]");
      if (pf) { pf.style.left = ((lo - pb.min) / span * 100).toFixed(1) + "%"; pf.style.width = ((hi - lo) / span * 100).toFixed(1) + "%"; }
      var a = rootEl.querySelector("[data-pmo-plo]"), b = rootEl.querySelector("[data-pmo-phi]");
      if (a) a.textContent = money(lo * 100);
      if (b) b.textContent = money(hi * 100);
    }
    clearTimeout(rangeMove.t);
    rangeMove.t = setTimeout(function () { renderResults(compute()); }, 120);
  }
  function rangeDone(el) {
    var key = el.getAttribute("data-range");
    if (key === "tLo" || key === "tHi") saveRider();
    S.shown = PER_PAGE;
    render();
  }

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && (S.fitOpen || S.sheet)) { S.fitOpen = false; S.sheet = null; lockScroll(); render(); }
  });

  loadData();
})();
